import { useCallback, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import Ionicons from "@expo/vector-icons/Ionicons";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { Avatar } from "@/components/ui/avatar";
import { IllustratedAvatar } from "@/components/ui/illustrated-avatar";
import { PROFILE_PIN_GLYPH, VIBE_GLYPH } from "@/lib/avatar-badge";
import { confirm, notify } from "@/lib/dialog";
import { type TranslationKey } from "@/i18n";
import {
  AVATAR_EYE_HEX,
  AVATAR_HAIR_HEX,
  AVATAR_SKIN_HEX,
  AVATAR_TOP_HEX,
  DEFAULT_AVATAR_CONFIG,
  avatarFieldOptions,
  randomAvatarConfig,
  resolveAvatarConfig,
  type AvatarField,
  type UserAvatarConfig,
} from "@/lib/avatar-config";
import { useAuthStore } from "@/state/auth-store";

const SWATCH_HEX: Partial<Record<AvatarField, Record<string, string>>> = {
  skinTone: AVATAR_SKIN_HEX,
  hairColor: AVATAR_HAIR_HEX,
  eyeColor: AVATAR_EYE_HEX,
  topColor: AVATAR_TOP_HEX,
};

/** Fields whose first option ("none") acts as a deselect. */
const DESELECTABLE: AvatarField[] = [
  "glasses",
  "accessory",
  "headCovering",
  "facialHair",
  "profilePin",
];

/**
 * Fields shown as their own symbol rather than as a mini avatar wearing the
 * option. Both of these ARE symbols, and a row of near-identical faces
 * differing only by a corner badge is unreadable at tile size — doubly so now
 * that the badge is drawn outside the illustration, where these tiles cannot
 * show it at all.
 */
const GLYPH_TILES: AvatarField[] = ["profilePin", "vibeIcon"];

const TILE_GLYPH: Record<string, Record<string, string>> = {
  profilePin: PROFILE_PIN_GLYPH,
  vibeIcon: VIBE_GLYPH,
};

type Category = {
  key: string;
  icon: keyof typeof Ionicons.glyphMap;
  swatch?: AvatarField;
  tiles: AvatarField[];
};

/** One category shown at a time; each pairs an optional color row with tiles. */
const CATEGORIES: Category[] = [
  { key: "skin", icon: "person-outline", swatch: "skinTone", tiles: ["faceShape"] },
  { key: "hair", icon: "cut-outline", swatch: "hairColor", tiles: ["hairStyle"] },
  { key: "eyes", icon: "eye-outline", swatch: "eyeColor", tiles: ["eyeStyle"] },
  { key: "brows", icon: "remove-outline", tiles: ["browStyle"] },
  { key: "nose", icon: "triangle-outline", tiles: ["noseStyle"] },
  { key: "lips", icon: "happy-outline", tiles: ["expression"] },
  { key: "glasses", icon: "glasses-outline", tiles: ["glasses"] },
  { key: "accessories", icon: "headset-outline", tiles: ["accessory"] },
  { key: "clothing", icon: "shirt-outline", swatch: "topColor", tiles: ["topStyle"] },
  {
    key: "extras",
    icon: "sparkles-outline",
    tiles: ["profilePin", "vibeIcon", "facialHair"],
  },
];

const HISTORY_CAP = 60;

export default function EditAvatarScreen() {
  const router = useRouter();
  const { t } = useTranslation();
  const { height } = useWindowDimensions();

  const user = useAuthStore((s) => s.user);
  const setAvatar = useAuthStore((s) => s.setAvatar);

  const seed = user?.avatarSeed ?? user?.id ?? user?.displayName ?? "opncue";
  const initial = useMemo(
    () => resolveAvatarConfig(user?.avatarConfig ?? null, seed),
    [user?.avatarConfig, seed],
  );

  const [draft, setDraft] = useState<UserAvatarConfig>(initial);
  const [past, setPast] = useState<UserAvatarConfig[]>([]);
  const [future, setFuture] = useState<UserAvatarConfig[]>([]);
  const [activeCat, setActiveCat] = useState(0);
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initial),
    [draft, initial],
  );

  const closeToProfile = useCallback(() => {
    router.replace("/profile");
  }, [router]);

  /** Push a new config, recording history for undo/redo. */
  const commit = useCallback((next: UserAvatarConfig) => {
    setDraft((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      setPast((p) => [...p.slice(-HISTORY_CAP), prev]);
      setFuture([]);
      return next;
    });
  }, []);

  function setField<K extends AvatarField>(field: K, value: UserAvatarConfig[K]) {
    commit({ ...draft, [field]: value });
  }

  const undo = useCallback(() => {
    setPast((p) => {
      if (p.length === 0) return p;
      const prev = p[p.length - 1];
      setDraft((cur) => {
        setFuture((f) => [cur, ...f].slice(0, HISTORY_CAP));
        return prev;
      });
      return p.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[0];
      setDraft((cur) => {
        setPast((p) => [...p.slice(-HISTORY_CAP), cur]);
        return next;
      });
      return f.slice(1);
    });
  }, []);

  async function onClose() {
    if (dirty) {
      const discard = await confirm({
        title: t("profile.avatar.discardChanges"),
        confirmLabel: t("profile.avatar.close"),
        cancelLabel: t("profile.avatar.cancel"),
        destructive: true,
      });
      if (!discard) return;
    }
    closeToProfile();
  }

  async function onSave() {
    if (saving) return;
    setSaving(true);
    try {
      await setAvatar(draft);
      notify(t("profile.avatar.saved"));
      closeToProfile();
    } catch {
      notify(t("profile.avatar.error"));
    } finally {
      setSaving(false);
    }
  }

  const category = CATEGORIES[activeCat];
  // Tray takes the lower ~46% of the screen; preview fills the rest.
  const trayHeight = Math.min(Math.max(height * 0.46, 320), 460);
  const isWeb = Platform.OS === "web";
  const avatarPreviewSize = isWeb
    ? Math.min(168, Math.max(144, Math.round(height * 0.22)))
    : 210;

  return (
    <View style={styles.root}>
      <LinearGradient
        colors={["#0B1B33", "#0A2547", "#0B1B33"]}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        style={StyleSheet.absoluteFill}
      />
      <StatusBar style="light" />

      <SafeAreaView style={styles.safe} edges={["top"]}>
        {/* Floating controls */}
        <View style={styles.topBar}>
          <Pressable
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={t("profile.avatar.closeA11y")}
            hitSlop={8}
            style={styles.closeBtn}
          >
            <Ionicons name="close" size={24} color="#EAF2FF" />
          </Pressable>

          <Pressable
            onPress={onSave}
            disabled={saving}
            accessibilityRole="button"
            accessibilityLabel={t("profile.avatar.saveA11y")}
            style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
          >
            {saving ? (
              <ActivityIndicator color="#fff" size="small" />
            ) : (
              <>
                <Ionicons name="checkmark" size={20} color="#fff" />
                <Text style={styles.saveText}>{t("profile.avatar.save")}</Text>
              </>
            )}
          </Pressable>
        </View>

        {/* Preview */}
        <View style={styles.previewArea}>
          <View
            style={[styles.previewGlow, isWeb && styles.previewGlowWeb]}
            accessible
            accessibilityRole="image"
            accessibilityLabel={t("profile.avatar.previewA11y")}
          >
            {/* Avatar, not IllustratedAvatar: the corner badge is drawn by
                Avatar, so this is the only way the preview shows the pin and
                vibe the way other people will see them. */}
            <Avatar
              avatarConfig={draft}
              size={avatarPreviewSize}
              badgeRingColor="#0B1B33"
            />
          </View>

          <View style={styles.undoRow}>
            <RoundBtn
              icon="arrow-undo"
              label={t("profile.avatar.undo")}
              disabled={past.length === 0}
              onPress={undo}
            />
            <RoundBtn
              icon="arrow-redo"
              label={t("profile.avatar.redo")}
              disabled={future.length === 0}
              onPress={redo}
            />
          </View>

          <Text style={styles.helper}>{t("profile.avatar.subtitle")}</Text>
        </View>
      </SafeAreaView>

      {/* Bottom customization tray */}
      <View style={[styles.tray, { height: trayHeight }]}>
        <View style={styles.handle} />

        {/* Category tabs. The strip keeps its full height whatever the
            selected category renders below it: without flexShrink 0 here (and
            flex 1 on the content scroller), a tall category — Extras, with
            three groups — squeezed the strip and clipped the tab labels. */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.tabScroll}
          contentContainerStyle={styles.tabRow}
        >
          {CATEGORIES.map((cat, i) => {
            const selected = i === activeCat;
            return (
              <Pressable
                key={cat.key}
                onPress={() => setActiveCat(i)}
                accessibilityRole="tab"
                accessibilityState={{ selected }}
                accessibilityLabel={t(
                  `profile.avatar.categories.${cat.key}` as TranslationKey,
                )}
                style={styles.tab}
              >
                <View style={[styles.tabIcon, selected && styles.tabIconActive]}>
                  <Ionicons
                    name={cat.icon}
                    size={20}
                    color={selected ? "#1E6BE0" : "#6B7785"}
                  />
                </View>
                <Text
                  numberOfLines={2}
                  maxFontSizeMultiplier={1.2}
                  style={[styles.tabLabel, selected && styles.tabLabelActive]}
                >
                  {t(`profile.avatar.categories.${cat.key}` as TranslationKey)}
                </Text>
                {selected ? <View style={styles.tabUnderline} /> : null}
              </Pressable>
            );
          })}
        </ScrollView>

        <View style={styles.trayDivider} />

        {/* Selected category controls */}
        <ScrollView
          style={styles.trayScroll}
          contentContainerStyle={styles.trayContent}
          showsVerticalScrollIndicator={false}
        >
          {category.swatch ? (
            <SwatchRow
              field={category.swatch}
              value={draft[category.swatch] as string}
              onSelect={(v) => setField(category.swatch as AvatarField, v as never)}
              t={t}
            />
          ) : null}

          {category.tiles.map((field) => (
            <TileGroup
              key={field}
              field={field}
              draft={draft}
              onSelect={(v) => setField(field, v as never)}
              showHeading={category.tiles.length > 1}
              t={t}
            />
          ))}

          {category.key === "extras" ? (
            <View style={styles.extrasActions}>
              <Pressable
                // Randomize changes the LOOK. A profile pin is a disclosure
                // the user made, not a look, so it survives — silently
                // dropping someone's access-need badge is exactly the kind of
                // thing they would not notice until it mattered. "Reset"
                // still clears it, because that one is explicit.
                onPress={() =>
                  commit({ ...randomAvatarConfig(), profilePin: draft.profilePin })
                }
                accessibilityRole="button"
                accessibilityLabel={t("profile.avatar.randomize")}
                style={styles.extrasBtn}
              >
                <Ionicons name="shuffle" size={18} color="#1E6BE0" />
                <Text style={styles.extrasBtnText}>{t("profile.avatar.randomize")}</Text>
              </Pressable>
              <Pressable
                onPress={() => commit({ ...DEFAULT_AVATAR_CONFIG })}
                accessibilityRole="button"
                accessibilityLabel={t("profile.avatar.reset")}
                style={styles.extrasBtn}
              >
                <Ionicons name="refresh-outline" size={18} color="#5A6675" />
                <Text style={[styles.extrasBtnText, { color: "#5A6675" }]}>
                  {t("profile.avatar.reset")}
                </Text>
              </Pressable>
            </View>
          ) : null}
        </ScrollView>
      </View>
    </View>
  );
}

function RoundBtn({
  icon,
  label,
  disabled,
  onPress,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled }}
      hitSlop={6}
      style={[styles.roundBtn, disabled && styles.roundBtnDisabled]}
    >
      <Ionicons name={icon} size={20} color={disabled ? "#4A5A70" : "#EAF2FF"} />
    </Pressable>
  );
}

function SwatchRow({
  field,
  value,
  onSelect,
  t,
}: {
  field: AvatarField;
  value: string;
  onSelect: (v: string) => void;
  t: (k: TranslationKey) => string;
}) {
  const hex = SWATCH_HEX[field] ?? {};
  const options = avatarFieldOptions(field) as readonly string[];
  const groupLabel = t(`profile.avatar.sections.${field}` as TranslationKey);
  return (
    <View style={styles.swatchBlock}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.swatchRow}
        accessibilityLabel={groupLabel}
      >
        {options.map((opt) => {
          const selected = opt === value;
          const label = t(`profile.avatar.options.${field}.${opt}` as TranslationKey);
          return (
            <Pressable
              key={opt}
              onPress={() => onSelect(opt)}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={
                selected ? `${label}, ${t("profile.avatar.selected")}` : label
              }
              style={styles.swatchWrap}
            >
              <View
                style={[
                  styles.swatch,
                  { backgroundColor: hex[opt] ?? "#CCD5E0" },
                  selected && styles.swatchSelected,
                ]}
              >
                {selected ? (
                  <Ionicons name="checkmark" size={16} color="#fff" />
                ) : null}
              </View>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

function TileGroup({
  field,
  draft,
  onSelect,
  showHeading,
  t,
}: {
  field: AvatarField;
  draft: UserAvatarConfig;
  onSelect: (v: string) => void;
  showHeading: boolean;
  t: (k: TranslationKey) => string;
}) {
  const options = avatarFieldOptions(field) as readonly string[];
  const current = draft[field] as string;
  const deselectable = DESELECTABLE.includes(field);
  const glyphTile = GLYPH_TILES.includes(field);
  return (
    <View style={styles.tileGroup}>
      {showHeading ? (
        <Text style={styles.tileHeading}>
          {t(`profile.avatar.sections.${field}` as TranslationKey)}
        </Text>
      ) : null}
      <View style={styles.tileGrid}>
        {options.map((opt) => {
          const selected = opt === current;
          const isNone = opt === "none";
          const label = t(`profile.avatar.options.${field}.${opt}` as TranslationKey);
          const a11y =
            deselectable && isNone
              ? t("profile.avatar.deselect")
              : selected
                ? `${label}, ${t("profile.avatar.selected")}`
                : label;
          return (
            <Pressable
              key={opt}
              onPress={() => onSelect(opt)}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={a11y}
              style={[styles.tile, selected && styles.tileSelected]}
            >
              {deselectable && isNone ? (
                <View style={styles.tileNone}>
                  <Ionicons name="ban-outline" size={26} color="#8A94A3" />
                </View>
              ) : glyphTile ? (
                <View style={styles.tileNone}>
                  <Text style={styles.tileGlyph} maxFontSizeMultiplier={1.3}>
                    {TILE_GLYPH[field]?.[opt] ?? ""}
                  </Text>
                </View>
              ) : (
                <View style={styles.tilePreview}>
                  <IllustratedAvatar config={{ ...draft, [field]: opt }} size={58} />
                </View>
              )}
              {selected ? (
                <View style={styles.tileCheck}>
                  <Ionicons name="checkmark" size={12} color="#fff" />
                </View>
              ) : null}
              <Text style={styles.tileLabel} numberOfLines={2}>
                {deselectable && isNone ? t("profile.avatar.deselect") : label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: "#0B1B33" },
  safe: { flex: 1 },
  topBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingTop: 4,
  },
  closeBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.10)",
  },
  saveBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minWidth: 96,
    height: 44,
    paddingHorizontal: 18,
    borderRadius: 22,
    backgroundColor: "#1E6BE0",
    justifyContent: "center",
  },
  saveBtnDisabled: { opacity: 0.7 },
  saveText: { color: "#fff", fontSize: 16, fontWeight: "700" },

  previewArea: { flex: 1, alignItems: "center", justifyContent: "center", gap: 14 },
  previewGlow: {
    padding: 8,
    borderRadius: 999,
    backgroundColor: "rgba(56,120,220,0.16)",
    shadowColor: "#2E6BE0",
    shadowOpacity: 0.5,
    shadowRadius: 40,
    shadowOffset: { width: 0, height: 0 },
  },
  previewGlowWeb: {
    padding: 6,
    shadowRadius: 28,
  },
  undoRow: { flexDirection: "row", gap: 14 },
  roundBtn: {
    width: 46,
    height: 46,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.08)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.12)",
  },
  roundBtnDisabled: { opacity: 0.45 },
  helper: {
    color: "#AFC4E4",
    fontSize: 14,
    textAlign: "center",
    lineHeight: 20,
    paddingHorizontal: 40,
  },

  tray: {
    backgroundColor: "#FFFFFF",
    borderTopLeftRadius: 26,
    borderTopRightRadius: 26,
    paddingTop: 8,
    shadowColor: "#000",
    shadowOpacity: 0.18,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: -4 },
    elevation: 12,
  },
  handle: {
    alignSelf: "center",
    width: 44,
    height: 5,
    borderRadius: 3,
    backgroundColor: "#D3DBE6",
    marginBottom: 8,
  },
  // flexGrow 0 / flexShrink 0: the strip is sized by its content and never
  // squeezed by what the selected category renders underneath it.
  tabScroll: { flexGrow: 0, flexShrink: 0 },
  tabRow: { paddingHorizontal: 12, gap: 4, alignItems: "flex-start" },
  tab: {
    alignItems: "center",
    width: 72,
    minHeight: 80,
    paddingVertical: 4,
    gap: 5,
  },
  tabIcon: {
    width: 46,
    height: 46,
    borderRadius: 23,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#F1F5FB",
  },
  tabIconActive: { backgroundColor: "#E3EEFF" },
  tabLabel: {
    width: "100%",
    minHeight: 28,
    fontSize: 11.5,
    color: "#6B7785",
    fontWeight: "600",
    lineHeight: 14,
    textAlign: "center",
  },
  tabLabelActive: { color: "#1E6BE0", fontWeight: "700" },
  tabUnderline: {
    width: 22,
    height: 3,
    borderRadius: 2,
    backgroundColor: "#1E6BE0",
  },
  trayDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: "#E6EBF2",
    marginTop: 6,
  },
  // Takes exactly the space the tray has left and scrolls inside it, so the
  // tray's height never depends on how many options a category has.
  trayScroll: { flex: 1 },
  trayContent: { padding: 16, paddingBottom: 40, gap: 14 },

  swatchBlock: { gap: 6 },
  swatchRow: { gap: 12, paddingVertical: 4, paddingRight: 8 },
  swatchWrap: { alignItems: "center" },
  swatch: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "rgba(0,0,0,0.08)",
  },
  swatchSelected: { borderColor: "#1E6BE0", borderWidth: 3 },

  tileGroup: { gap: 8 },
  tileHeading: { fontSize: 14, fontWeight: "700", color: "#1B2733" },
  tileGrid: { flexDirection: "row", flexWrap: "wrap", gap: 10 },
  tile: {
    width: 74,
    minHeight: 94,
    alignItems: "center",
    gap: 4,
    padding: 6,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: "#E6EBF2",
    backgroundColor: "#F8FAFD",
    position: "relative",
  },
  tileSelected: { borderColor: "#1E6BE0", backgroundColor: "#EEF5FF" },
  tilePreview: {
    width: 58,
    height: 58,
    borderRadius: 12,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  tileNone: {
    width: 58,
    height: 58,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#EEF1F6",
  },
  // Capped multiplier: the glyph shares a fixed-size tile with its label, and
  // at large accessibility text sizes an uncapped emoji overflows the tile.
  tileGlyph: { fontSize: 28, lineHeight: 34 },
  tileCheck: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1E6BE0",
  },
  tileLabel: {
    fontSize: 11,
    color: "#5A6675",
    fontWeight: "600",
    lineHeight: 13,
    textAlign: "center",
  },

  extrasActions: { flexDirection: "row", gap: 12, marginTop: 4 },
  extrasBtn: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 12,
    backgroundColor: "#F1F5FB",
  },
  extrasBtnText: { fontSize: 14, fontWeight: "700", color: "#1E6BE0" },
});
